/**
 * Bulk Classify Modal
 * Modal for classifying multiple holdings at once
 */
import { useState } from 'react';
import { useBulkClassifyHoldings, useHoldings } from '@/hooks/useAllocation';
import { useAllocationStore, selectSelectedHoldingIds } from '@/store/allocationStore';
import Button from '@/components/ui/Button';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  type AssetClass,
  type BulkClassifyHoldingsRequest,
} from '@/types/allocation';

interface BulkClassifyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BulkClassifyModal({
  isOpen,
  onClose,
}: BulkClassifyModalProps) {
  const selectedIds = useAllocationStore(selectSelectedHoldingIds);
  const clearSelection = useAllocationStore((state) => state.clearHoldingSelection);
  const { data: holdingsData } = useHoldings();
  const { mutate: bulkClassify, isPending } = useBulkClassifyHoldings();

  // Form state
  const [assetClass, setAssetClass] = useState<AssetClass>('other');

  // Get selected holdings details
  const selectedHoldings = holdingsData?.holdings.filter((h) =>
    selectedIds.includes(h.holdingId)
  ) ?? [];

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (selectedIds.length === 0) return;

    const data: BulkClassifyHoldingsRequest = {
      classifications: selectedIds.map((holdingId) => ({
        holdingId,
        assetClass,
      })),
    };
    bulkClassify(data, {
      onSuccess: () => {
        clearSelection();
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setAssetClass('other');
    onClose();
  };

  const totalValue = selectedHoldings.reduce(
    (sum, h) => {
      const val = h.currentValue;
      if (val == null || val === '') return sum;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return sum + (Number.isNaN(num) ? 0 : num);
    },
    0
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Bulk Classify Holdings
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Summary */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">
                {selectedIds.length} holding{selectedIds.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-sm text-blue-700">
                Total value: ${formatNumber(totalValue)}
              </p>
            </div>

            {/* Selected holdings list */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Holdings
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-200">
                {selectedHoldings.map((holding) => (
                  <div key={holding.holdingId} className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">
                      {holding.securityName}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{holding.ticker || 'No ticker'}</span>
                      <span>${formatNumber(holding.currentValue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Asset class selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classify All As
              </label>
              <select
                value={assetClass}
                onChange={(e) => setAssetClass(e.target.value as AssetClass)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {Object.entries(ASSET_CLASS_DISPLAY_NAMES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                All selected holdings will be classified as this asset class
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={selectedIds.length === 0}
              isLoading={isPending}
            >
              Classify {selectedIds.length} Holding{selectedIds.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
